import {
  NODE_WIDTH,
  NODE_HEIGHT,
  EXPANDED_ACTIONS_WIDTH,
  DATA_RETRIEVER_MIN_WIDTH,
} from './consts';
import { RESOURCE_TYPE_CREDENTIAL_GROUP } from 'js/constants/resourceTypes';
import { isNil } from 'lodash';
import type { FlowNode, NodeBuilderOptions, NodePosition } from './types';
import { Node } from '@xyflow/react';

export function buildFlowNode(
  item: any,
  options: NodeBuilderOptions & { parentId?: string; isSecondaryFlow?: boolean },
): Node {
  const { parentId, aclQueryMode, pipelineItems, nodeProps, additionalNodeProps, isSecondaryFlow } =
    options;

  const flowId = item.flow_node_id || item.id;

  const id = 'flow-' + flowId;
  const expanded = nodeProps.expandedNodes.includes(id);

  return {
    id,
    type: isSecondaryFlow ? 'secondary_flow' : 'flow',
    position: { x: 0, y: 0 },
    parentId,
    selected: isSecondaryFlow && expanded,
    zIndex: isSecondaryFlow && expanded ? 2000 : 0,
    data: {
      item,
      expanded,
      height: (item.height || 1) * NODE_HEIGHT,
      width: (item.width || 1) * NODE_WIDTH,
      key: flowId,
      nodeKey: flowId,
      aclQueryMode: aclQueryMode,
      items: pipelineItems,
      isRoot: item.type === 'SOURCE',
      ...nodeProps,
      ...additionalNodeProps,
    },
  };
}

interface CredentialNodeOptions extends NodeBuilderOptions {
  parentId?: string;
  entities: Record<string, any>;
  isRagFlow?: boolean;
}

export function buildCredentialNode(item: any, options: CredentialNodeOptions): FlowNode | null {
  const { parentId, entities, nodeProps, additionalNodeProps = {}, isRagFlow = false } = options;

  const isAdaptiveFlow = item?.['adaptive_flow'] === true;

  let credentialsData;
  if (item?.data_credentials_group_id && isAdaptiveFlow) {
    credentialsData = {
      ...entities?.credentialsGroups?.[item?.data_credentials_group_id],
      primaryCredentialId: item?.data_credentials_id,
    };
  } else if (item?.data_credentials_id) {
    credentialsData = entities?.credentials?.[item?.data_credentials_id];
  }

  if (isNil(credentialsData)) {
    return null;
  }

  return {
    id: `credential-${item.flow_node_id}`,
    type: RESOURCE_TYPE_CREDENTIAL_GROUP,
    position: { x: 0, y: 0 },
    parentId,
    zIndex: 1,
    data: {
      item,
      credentialsData: credentialsData,
      isAdaptiveFlow: isAdaptiveFlow,
      key: item.flow_node_id,
      nodeKey: item.flow_node_id,
      parentId,
      ...nodeProps,
      ...additionalNodeProps,
    },
  };
}

export function buildResourceNode(
  item: any,
  options: NodeBuilderOptions & {
    parentId?: string;
    expanded?: boolean;
    expandedSource?: any;
    additionalNodeProps?: Record<string, any>;
  },
): Node {
  const {
    nodeProps,
    parentId,
    expanded = false,
    expandedSource,
    additionalNodeProps = {},
  } = options;
  const nodeType = item.nodeType || item.node_type || 'resource';
  let width = NODE_WIDTH;

  if (nodeType === 'data_retriever_holder') {
    // FIXED: Always use DATA_RETRIEVER_MIN_WIDTH for initial calculation
    // The depth-based formula was causing huge mismatches (1020px vs 350px actual)
    // Runtime calculation (in DataRetriever.tsx) will expand if needed based on actual child positions
    width = DATA_RETRIEVER_MIN_WIDTH;

    const hasRetrieverFlows = item.retriever_flows && item.retriever_flows.length > 0;

    if (hasRetrieverFlows) {
      // Log info for debugging, but don't use depth-based calculation
      const retrieverFlowsInfo = item.retriever_flows.map((flow: any, idx: number) => {
        const flowNodes = Object.values(flow);
        const flowMaxDepth = flowNodes.reduce((max: number, node: any) => {
          return node?.depth > max ? node?.depth : max;
        }, 0);
        return {
          flowIndex: idx,
          flowNodeCount: flowNodes.length,
          flowMaxDepth,
          flowNodeDepths: flowNodes.map((n: any) => n?.depth),
        };
      });

      const maxDepth =
        retrieverFlowsInfo.reduce((max: number, info: any) => {
          return info.flowMaxDepth > max ? info.flowMaxDepth : max;
        }, 0) || 0;

      // console.log('[RETRIEVER_WIDTH] FIXED - Using min width:', {
      //   nodeId: item.flow_node_id,
      //   retrieverFlowsCount: item.retriever_flows.length,
      //   retrieverFlowsInfo,
      //   maxDepth,
      //   newWidth: width, // What we're using now
      //   DATA_RETRIEVER_MIN_WIDTH,
      //   note: 'Runtime calculation will expand if children need more space'
      // });
    } else {
      // console.log('[RETRIEVER_WIDTH] Empty state:', {
      //   nodeId: item.flow_node_id,
      //   width,
      //   DATA_RETRIEVER_MIN_WIDTH,
      //   formula: 'DATA_RETRIEVER_MIN_WIDTH'
      // });
    }
  }

  return {
    id: item.flow_node_id ? 'node-' + item.flow_node_id : `node-${item.type}-${item.id}`,
    type: nodeType,
    position: { x: 0, y: 0 },
    zIndex: 2,
    parentId,
    connectable: true,
    data: {
      item,
      flow: item.flow,
      resourceType: item.resourceType || item.resource_type,
      expanded,
      expandedSource,
      height: NODE_HEIGHT,
      width,
      key: item.flow_node_id,
      nodeKey: item.flow_node_id,
      aclQueryMode: options.aclQueryMode,
      items: options.pipelineItems,
      // Mark as root if it's a SOURCE (regular flows) or query_prompt (RAG flows)
      isRoot: item.type === 'SOURCE' || item.node_type === 'query_prompt',
      node_type: nodeProps.node_type,
      ...additionalNodeProps,
      ...nodeProps,
      parentId,
      size: item.size || nodeProps.size,
    },
  } as Node;
}

/**
 * Builds the synthetic "Code" node for BYO (`flow_type: 'custom'`) flows.
 *
 * BYO flows have no detected nexset, so the canvas shows Source → **Code** →
 * Destination. The code node is a normal (dagre-laid) node — it participates in
 * layout via the source→code→sink edges {@link createEdges} produces — so it
 * sits inline between the source and any sink, exactly where the nexset node
 * would be. Its id is derived from the source's flow_node_id so edges can
 * target it deterministically.
 */
export function buildCodeNode(
  item: any,
  options: {
    parentId?: string;
    nodeProps: any;
    aclQueryMode?: any;
  },
): Node {
  const { parentId, nodeProps, aclQueryMode } = options;

  return {
    id: 'code-' + item.flow_node_id,
    type: 'code',
    position: { x: 0, y: 0 },
    zIndex: 2,
    parentId,
    connectable: false,
    data: {
      item,
      flowId: item.origin_node_id ?? item.flow_node_id,
      key: 'code-' + item.flow_node_id,
      nodeKey: item.flow_node_id,
      aclQueryMode,
      parentId,
      ...nodeProps,
    },
  } as Node;
}

/**
 * Builds the dashed SOURCE shell node for the BYO create flow (before a real
 * source is configured). Registered as type 'byoSourceShell' in FlowNodes.
 */
export function buildByoSourceShellNode(
  item: any,
  options: {
    parentId?: string;
    nodeProps: any;
    aclQueryMode?: any;
  },
): Node {
  const { parentId, nodeProps, aclQueryMode } = options;
  // Use 'node-' prefix so createCodeNodeEdge (which builds edge-code-* edges
  // using source: 'node-' + flow_node_id) can wire SOURCE→CODE automatically.
  const id = 'node-' + item.flow_node_id;
  return {
    id,
    type: 'byoSourceShell',
    position: { x: 0, y: 0 },
    zIndex: 2,
    parentId,
    connectable: false,
    data: {
      item,
      flowId: item.origin_node_id ?? item.flow_node_id,
      key: id,
      nodeKey: item.flow_node_id,
      aclQueryMode,
      parentId,
      ...nodeProps,
      isRoot: true,
    },
  } as Node;
}

/**
 * Builds the dashed DESTINATION shell node for BYO flows that have no sink yet.
 * Registered as type 'byoDestShell' in FlowNodes. Dagre lays it out right of
 * the code node; clicking it opens the create-sink modal.
 */
export function buildByoDestShellNode(
  item: any,
  options: {
    parentId?: string;
    nodeProps: any;
    aclQueryMode?: any;
  },
): Node {
  const { parentId, nodeProps, aclQueryMode } = options;
  const id = 'byo-dest-shell-' + item.flow_node_id;
  return {
    id,
    type: 'byoDestShell',
    position: { x: 0, y: 0 },
    zIndex: 2,
    parentId,
    connectable: false,
    data: {
      item,
      flowId: item.origin_node_id ?? item.flow_node_id,
      key: id,
      nodeKey: item.flow_node_id,
      aclQueryMode,
      parentId,
      ...nodeProps,
    },
  } as Node;
}

export function buildNexsetLoaderNode(
  item: any,
  options: {
    parentId?: string;
    nodeProps: any;
    isSqlAutomation?: boolean;
  },
): FlowNode {
  const { parentId, nodeProps, isSqlAutomation } = options;
  const position = { x: 0, y: 0 };

  const id = item.flow_node_id
    ? 'nexset-loader-' + item.flow_node_id
    : `nexset-loader-${item.type}-${item.id}`;

  return {
    id,
    type: 'nexsetLoader',
    zIndex: 2,
    position: position as NodePosition,
    parentId,
    data: {
      item,
      id,
      position,
      parentId,
      source: item,
      isSqlAutomation,
      ...nodeProps,
    },
  };
}
